import { Button } from '@/global/components/Button'
import IconButton from '@/global/components/Button/IconButton'
import { CardAssociacoes } from '@/global/components/CardAssociacoes'
import CircularProgress from '@/global/components/CircularProgress'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { GridCellParams } from '@/global/components/DataGrid/utils/types'
import { Tooltip } from '@/global/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { ContentGuide } from 'global/guides/ContentGuide'
import { useDebouncedValue } from 'hooks'
import { hasPermission } from 'hooks/useBackendMenu'
import { SendIcon } from 'lucide-react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { reps } from 'service/api'
import { toast } from 'sonner'
import { AdicionarFuncionariosRep } from './modals/AdicionarFuncionarios'
import { DesassociarFuncionarioRep } from './modals/DesassociarFuncionario'

/** Janela de agrupamento dos cliques de reenvio, em milissegundos. */
const JANELA_REENVIO = 800

interface IEnvioContext {
    estaEnviando: (funcionarioId: number) => boolean
    solicitarEnvio: (funcionarioId: number) => void
}

const EnvioContext = createContext<IEnvioContext>({
    estaEnviando: () => false,
    solicitarEnvio: () => {},
})

/**
 * A célula precisa ser um componente estável, declarado fora do render: o
 * DataTable captura os colDefs no mount (`useColunasPersistidas`), então um
 * `renderCell` inline congelaria o estado do primeiro render e o botão nunca
 * trocaria para o spinner. O estado do envio chega por contexto.
 */
const EnviarFuncionarioCell: React.FC<GridCellParams> = ({ row }) => {
    const { estaEnviando, solicitarEnvio } = useContext(EnvioContext)
    const enviando = estaEnviando(row.funcionarioId)

    return (
        <IconButton
            title="Enviar ao equipamento"
            onClick={() => solicitarEnvio(row.funcionarioId)}
            disabled={enviando}
            className="relative"
        >
            <CircularProgress
                className={cn(
                    'absolute size-6 top-1/5 right-1/5 opacity-0',
                    'transition-opacity duration-500',
                    {
                        'opacity-100': enviando,
                    },
                )}
            />
            <SendIcon
                className={cn(
                    'size-6 opacity-100',
                    'transition-opacity duration-500',
                    {
                        'opacity-0': enviando,
                    },
                )}
            />
        </IconButton>
    )
}

interface Props {
    repId: number
}

/** Funcionários vinculados ao REP, com adicionar, remover e reenviar ao equipamento. */
export function FuncionariosAssociadosRep({ repId }: Props) {
    const [isAdicionarAberto, setIsAdicionarAberto] = useState(false)
    const canEditar = hasPermission(['update', 'Funcionários'])
    const canEnviar = hasPermission(['update', 'Reps'])
    const { data, isLoading } = reps.funcionarios.useFetchById(repId)
    const { mutate: enviar, isLoading: isEnviando } =
        reps.enviar.mutations.useCreate()
    const [pendentes, setPendentes] = useState<number[]>([])
    /** Ids já despachados ao equipamento, aguardando resposta. */
    const [emVoo, setEmVoo] = useState<number[]>([])
    const pendentesDebounced = useDebouncedValue(pendentes, JANELA_REENVIO)

    const associados = (data as any)?.FuncionarioReps ?? []

    const enviarFuncionarios = (funcionarioIds?: number[]) => {
        enviar(
            { repId, funcionarioIds },
            {
                onSuccess: ({ enviados, success, message }) => {
                    if (!success) {
                        toast.error(
                            message ??
                                'Equipamento não recebeu os funcionários enviados',
                        )
                        return
                    }

                    toast.success(
                        `${enviados} funcionário(s) enviado(s) ao equipamento`,
                    )
                },
                onError: err => {
                    toast.error(
                        err.message ||
                            'Erro ao enviar os funcionário ao equipamento.',
                    )
                },
                onSettled: () => {
                    if (!funcionarioIds?.length) return

                    setEmVoo(prev =>
                        prev.filter(it => !funcionarioIds.includes(it)),
                    )
                },
            },
        )
    }

    // cliques em linhas diferentes viram um único envio em bloco
    useEffect(() => {
        if (!pendentesDebounced.length) return

        setEmVoo(prev => [...prev, ...pendentesDebounced])
        enviarFuncionarios(pendentesDebounced)
        setPendentes([])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendentesDebounced])

    const envio = useMemo<IEnvioContext>(
        () => ({
            estaEnviando: funcionarioId =>
                pendentes.includes(funcionarioId) ||
                emVoo.includes(funcionarioId),
            solicitarEnvio: funcionarioId =>
                setPendentes(prev =>
                    prev.includes(funcionarioId)
                        ? prev
                        : [...prev, funcionarioId],
                ),
        }),
        [pendentes, emVoo],
    )

    const isEnviarDisabled = isEnviando || associados.length === 0
    const Comp = useMemo(
        () => (isEnviarDisabled ? 'div' : Tooltip),
        [isEnviarDisabled],
    )

    return (
        <EnvioContext.Provider value={envio}>
            <CardAssociacoes
                titulo="Funcionários associados"
                tituloAdicionar="Adicionar funcionários"
                onAdicionar={
                    canEditar ? () => setIsAdicionarAberto(true) : undefined
                }
                acoes={
                    canEnviar && (
                        <Comp title="Envia todos os funcionários para o equipamento (se compatível)">
                            <Button
                                variant="outlined"
                                startIcon={<SendIcon />}
                                onClick={() =>
                                    isEnviarDisabled
                                        ? undefined
                                        : enviarFuncionarios()
                                }
                                disabled={isEnviarDisabled}
                            >
                                Enviar todos
                            </Button>
                        </Comp>
                    )
                }
            >
                <div className="min-h-96 w-full flex-1">
                    <ContentGuide.DataGrid
                        loading={isLoading}
                        density="compact"
                        className="*:border-none"
                        columns={[
                            {
                                field: 'Funcionario',
                                headerName: 'Nome',
                                flex: 1,
                                valueGetter: params =>
                                    params.row.Funcionario.nome,
                            },
                            ...(canEnviar
                                ? [
                                      {
                                          ...optionsColumn,
                                          field: '__enviar',
                                          renderCell: EnviarFuncionarioCell,
                                      },
                                  ]
                                : []),
                            ...(canEditar
                                ? [
                                      {
                                          ...optionsColumn,
                                          field: '__desassociar',
                                          renderCell: DesassociarFuncionarioRep,
                                      },
                                  ]
                                : []),
                        ]}
                        rows={associados}
                        ToolbarProps={{ hideToolbar: true }}
                        hideFooter
                    />
                </div>
            </CardAssociacoes>
            <AdicionarFuncionariosRep
                repId={repId}
                funcionarioIdsAssociados={associados.map(
                    (item: any) => item.funcionarioId,
                )}
                open={isAdicionarAberto}
                onClose={() => setIsAdicionarAberto(false)}
            />
        </EnvioContext.Provider>
    )
}
